import React, { useMemo, useState } from 'react';
import { ArrowLeft, ChevronRight, Loader2, Lock, Save, Shield, Trash2 } from 'lucide-react';
import { roleService } from '../../services/roleService';
import { useAuth } from '../../context/AuthContext';
import { diff, unitLabel } from '../../lib/permissions';
import PermissionPicker, { MenuPreview } from './PermissionPicker';
import { DeleteModal } from '../shared/DeleteModal';

export function ConfirmDialog({ title, body, confirmLabel, onConfirm, onCancel }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onCancel}>
            <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                <p className="mt-2 text-gray-600">{body}</p>
                <div className="mt-6 flex justify-end gap-3">
                    <button onClick={onCancel} className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50">Cancel</button>
                    <button onClick={onConfirm} className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">{confirmLabel}</button>
                </div>
            </div>
        </div>
    );
}

const RoleEditor = ({ catalog, role, roles, users, onBack, onSaved }) => {
    const { hasPermission } = useAuth();
    const isNew = !role;
    const fixed = role && !role.editable;
    const initial = useMemo(() => ({
        label: role ? role.label || role.name : '',
        description: role?.description || '',
        permissions: role ? role.permissionNames || [] : ['DASHBOARD:READ'],
    }), [role]);
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [confirmDiscard, setConfirmDiscard] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const canEdit = !fixed && hasPermission(isNew ? 'ROLES:CREATE' : 'ROLES:UPDATE');
    const canEditPermissions = canEdit && (isNew || hasPermission('ROLES:MANAGE'));
    const changes = diff(initial.permissions, form.permissions);
    const dirty = form.label.trim() !== initial.label || form.description.trim() !== initial.description
        || changes.adds.length > 0 || changes.removes.length > 0;
    const holders = role ? users.filter((u) => u.roleName === role.name) : [];
    const members = role?.members ?? 0;

    const taken = roles.some((r) => r.id !== role?.id && (r.label || r.name).toLowerCase() === form.label.trim().toLowerCase());
    const reason = !form.label.trim() ? 'Give the role a name'
        : taken ? 'Another role already has this name'
        : form.permissions.length === 0 ? 'Switch on at least one permission'
        : null;

    const back = () => (dirty && canEdit ? setConfirmDiscard(true) : onBack());

    const save = async () => {
        if (reason) return;
        setSaving(true);
        setError('');
        try {
            const body = { label: form.label.trim(), description: form.description.trim() };
            if (canEditPermissions) body.permissions = form.permissions;
            const res = isNew ? await roleService.create(body) : await roleService.update(role.id, body);
            if (res && res.success === false) throw new Error(res.message);
            await onSaved();
        } catch (err) {
            setError(err.message || 'Failed to save role');
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        setSaving(true);
        setError('');
        try {
            await roleService.delete(role.id);
            setConfirmDelete(false);
            await onSaved();
        } catch (err) {
            setConfirmDelete(false);
            setError(err.message || 'Failed to delete role');
        } finally {
            setSaving(false);
        }
    };

    const field = 'w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500';

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-3">
                <button onClick={back} className="rounded-lg p-2 text-gray-600 hover:bg-gray-100" title="Back to roles">
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <nav className="flex items-center gap-2 text-sm text-gray-500">
                    <button onClick={back} className="hover:text-gray-800">Roles & Permissions</button>
                    <ChevronRight className="w-4 h-4" />
                </nav>
                <h2 className="text-2xl font-semibold text-gray-900">{form.label.trim() || (isNew ? 'New role' : role.name)}</h2>
                <div className="ml-auto flex items-center gap-3">
                    {role && !role.builtIn && hasPermission('ROLES:DELETE') && (
                        <button
                            onClick={() => setConfirmDelete(true)}
                            disabled={members > 0 || saving}
                            title={members > 0 ? 'Give its people another role first' : 'Delete role'}
                            className="flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <Trash2 className="w-4 h-4" />Delete
                        </button>
                    )}
                    {canEdit ? (
                        <button
                            onClick={save}
                            disabled={!!reason || saving || (!isNew && !dirty)}
                            title={reason || undefined}
                            className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 text-white hover:from-blue-700 hover:to-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            {isNew ? 'Create role' : 'Save role'}
                        </button>
                    ) : (
                        <button onClick={onBack} className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50">Back to roles</button>
                    )}
                </div>
            </div>

            {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_300px]">
                <div className="space-y-6 rounded-xl border border-gray-200 bg-white p-6">
                    <section>
                        <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-500">About the role</h3>
                        <div className="grid gap-4 md:grid-cols-2">
                            <label className="block">
                                <span className="mb-1 block text-sm font-medium text-gray-700">Name <span className="text-red-500">*</span></span>
                                <input className={field} value={form.label} maxLength={60} disabled={!canEdit} placeholder="Customer Service"
                                    onChange={(e) => setForm({ ...form, label: e.target.value })} />
                            </label>
                            <label className="block">
                                <span className="mb-1 block text-sm font-medium text-gray-700">Description <span className="font-normal text-gray-400">optional</span></span>
                                <input className={field} value={form.description} maxLength={200} disabled={!canEdit} placeholder="Answers client calls and updates their details"
                                    onChange={(e) => setForm({ ...form, description: e.target.value })} />
                            </label>
                        </div>
                    </section>

                    <section className="border-t border-gray-200 pt-6">
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">What they can do</h3>
                        {fixed ? (
                            <div className="mt-3 flex items-start gap-3 rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
                                <Lock className="mt-0.5 w-4 h-4 shrink-0" />
                                <p>{role.label || role.name} can do everything in Nexus, including managing users and roles. It can't be changed, so there is always someone who can.</p>
                            </div>
                        ) : (
                            <>
                                <p className="mb-4 mt-1 text-sm text-gray-500">Switch on each part of Nexus this role needs. Their menu on the right updates as you go.</p>
                                <PermissionPicker
                                    catalog={catalog}
                                    value={form.permissions}
                                    onChange={(permissions) => setForm({ ...form, permissions })}
                                    disabled={!canEditPermissions}
                                />
                            </>
                        )}
                    </section>
                </div>

                <aside className="space-y-4 xl:sticky xl:top-4 xl:self-start">
                    {!fixed && <MenuPreview permissions={form.permissions} />}
                    {!isNew && (
                        <div className="rounded-xl border border-gray-200 bg-white p-4">
                            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">People with this role</p>
                            {holders.length ? (
                                <ul className="mt-3 space-y-2">
                                    {holders.map((u) => (
                                        <li key={u.id} className="flex items-center justify-between gap-2 text-sm">
                                            <span className="truncate text-gray-800">{u.firstName} {u.lastName}</span>
                                            {u.ownPermissions && <span className="shrink-0 text-xs font-medium text-amber-700">Own permissions</span>}
                                        </li>
                                    ))}
                                </ul>
                            ) : members > 0 ? (
                                <p className="mt-2 text-sm text-gray-600">{members} {members === 1 ? 'person' : 'people'}</p>
                            ) : (
                                <p className="mt-2 text-sm text-gray-500">Nobody yet. Give someone this role from Users.</p>
                            )}
                            {holders.some((u) => u.ownPermissions) && (
                                <p className="mt-3 text-xs text-gray-500">People with their own permissions don't get changes made to the role.</p>
                            )}
                        </div>
                    )}
                    {!isNew && (changes.adds.length > 0 || changes.removes.length > 0) && (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm">
                            <p className="text-xs font-semibold uppercase tracking-wider text-amber-800">Unsaved changes</p>
                            {changes.adds.length > 0 && (
                                <p className="mt-2 text-gray-700"><span className="font-medium text-green-700">Adds:</span> {changes.adds.map((u) => unitLabel(catalog, u)).join(', ')}</p>
                            )}
                            {changes.removes.length > 0 && (
                                <p className="mt-2 text-gray-700"><span className="font-medium text-red-700">Removes:</span> {changes.removes.map((u) => unitLabel(catalog, u)).join(', ')}</p>
                            )}
                            <p className="mt-2 text-xs text-gray-500">People get the change the next time their session refreshes.</p>
                        </div>
                    )}
                    {isNew && (
                        <div className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-500">
                            <Shield className="mt-0.5 w-4 h-4 shrink-0 text-blue-600" />
                            <p>After creating it, give people this role from Users.</p>
                        </div>
                    )}
                </aside>
            </div>

            {confirmDiscard && (
                <ConfirmDialog
                    title="Discard changes?"
                    body="Your changes to this role haven't been saved."
                    confirmLabel="Discard"
                    onCancel={() => setConfirmDiscard(false)}
                    onConfirm={onBack}
                />
            )}
            <DeleteModal
                isOpen={confirmDelete}
                onClose={() => setConfirmDelete(false)}
                onConfirm={remove}
                itemName={role?.label || role?.name}
                itemType="role"
                loading={saving}
            />
        </div>
    );
};

export default RoleEditor;
